if (!customElements.get('product-finder')) {
  class ProductFinder extends HTMLElement {
    constructor() {
      super();

      this.steps = Array.from(this.querySelectorAll('[data-product-finder-step]'));
      this.progressDots = Array.from(this.parentElement.querySelectorAll('[data-product-finder-progress] li'));
      this.currentStepIndex = 0;
      this.previewVisible = false;
      this.skipChoicesOnBack = false;

      this.finalFilterParam = this.dataset.finalFilterParam;
      this.extraFilterParams = this.dataset.extraFilterParams || '';
      this.excludedValues = (this.dataset.excludedValues || '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean);

      this.selection = {
        audienceParams: '',
        collectionUrl: '',
        finalParamName: undefined,
        finalValue: undefined
      };

      this.selectedAudienceParams = new Set();
      this.continueButton = this.querySelector('[data-product-finder-continue]');

      const finalStep = this.steps.find(step => step.dataset.productFinderStep === 'final');

      this.finalStep = finalStep;
      this.choicesEl = finalStep.querySelector('[data-product-finder-choices]');
      this.optionsContainer = finalStep.querySelector('[data-product-finder-options]');
      this.loadingEl = finalStep.querySelector('[data-product-finder-loading]');
      this.unknownButton = finalStep.querySelector('[data-product-finder-unknown]');
      this.emptyEl = finalStep.querySelector('[data-product-finder-empty]');
      this.emptyLink = this.emptyEl.querySelector('a');
      this.previewEl = finalStep.querySelector('[data-product-finder-preview]');
      this.previewLoadingEl = finalStep.querySelector('[data-product-finder-preview-loading]');
      this.previewGrid = finalStep.querySelector('[data-product-finder-preview-grid]');
      this.previewSlider = finalStep.querySelector('card-product-slider');
      this.previewEmptyEl = finalStep.querySelector('[data-product-finder-preview-empty]');
      this.seeAllLink = finalStep.querySelector('[data-product-finder-see-all]');
      this.seeAllLabel = this.seeAllLink.textContent.trim();

      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(event) {
      const backButton = event.target.closest('[data-product-finder-back]');

      if (backButton) {
        if (this.skipChoicesOnBack) {
          this.goToStep(0);
        } else if (this.previewVisible) {
          this.showChoices();
        } else {
          this.goToStep(this.currentStepIndex - 1);
        }
        return;
      }

      const unknownButton = event.target.closest('[data-product-finder-unknown]');

      if (unknownButton) {
        this.selection.finalParamName = undefined;
        this.selection.finalValue = undefined;
        this.showPreview();
        return;
      }

      const continueButton = event.target.closest('[data-product-finder-continue]');

      if (continueButton) {
        this.handleContinue();
        return;
      }

      const optionButton = event.target.closest('[data-product-finder-option]');

      if (optionButton) {
        this.selectOption(optionButton);
      }
    }

    selectOption(optionButton) {
      const step = optionButton.closest('[data-product-finder-step]');
      const stepType = step.dataset.productFinderStep;

      if (stepType === 'audience') {
        this.toggleAudienceOption(optionButton);
      } else if (stepType === 'budget') {
        this.selection.collectionUrl = optionButton.dataset.collectionUrl || '';
        this.goToStep(this.currentStepIndex + 1);
        this.loadFinalStep();
      } else if (stepType === 'final') {
        this.selection.finalParamName = optionButton.dataset.paramName;
        this.selection.finalValue = optionButton.dataset.value;
        this.showPreview();
      }
    }

    toggleAudienceOption(optionButton) {
      const filterParams = optionButton.dataset.filterParams || '';
      const isSelected = this.selectedAudienceParams.has(filterParams);

      if (isSelected) {
        this.selectedAudienceParams.delete(filterParams);
      } else {
        this.selectedAudienceParams.add(filterParams);
      }

      optionButton.classList.toggle('button--filled', !isSelected);
      optionButton.classList.toggle('button--outlined', isSelected);

      this.continueButton.disabled = this.selectedAudienceParams.size === 0;
    }

    handleContinue() {
      if (this.selectedAudienceParams.size === 0) return;

      this.selection.audienceParams = Array.from(this.selectedAudienceParams).join('&');
      this.goToStep(this.currentStepIndex + 1);
    }

    goToStep(index) {
      if (index < 0 || index >= this.steps.length || index === this.currentStepIndex) return;

      const oldStep = this.steps[this.currentStepIndex];
      const newStep = this.steps[index];

      this.currentStepIndex = index;

      this.progressDots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });

      this.hide(oldStep, () => this.show(newStep));
    }

    async loadFinalStep() {
      if (!this.selection.collectionUrl) return;

      this.showChoices();

      const fallbackUrl = this.buildResultsUrl();

      this.optionsContainer.innerHTML = '';
      this.hide(this.emptyEl);
      this.show(this.unknownButton);
      this.show(this.loadingEl);
      this.skipChoicesOnBack = false;

      try {
        const requestParams = this.buildParams();

        requestParams.set('view', 'finder');

        const response = await fetch(`${this.selection.collectionUrl}?${requestParams.toString()}`);

        if (!response.ok) throw new Error('Product finder request failed');

        const filters = await response.json();
        const targetFilter = filters.find(filter => filter.param_name === this.finalFilterParam);
        const values = (targetFilter && targetFilter.values ? targetFilter.values : []).filter(
          value => !this.excludedValues.includes(value.label)
        );

        this.hide(this.loadingEl);

        if (values.length === 0) {
          this.emptyLink.href = fallbackUrl;
          this.hide(this.unknownButton);
          this.show(this.emptyEl);
          this.skipChoicesOnBack = true;
          return;
        }

        values.forEach(value => this.renderTypeOption(value));

        if (values.length === 1) {
          this.hide(this.unknownButton);
          this.selection.finalParamName = values[0].param_name;
          this.selection.finalValue = values[0].value;
          this.skipChoicesOnBack = true;
          this.showPreview();
        }
      } catch (error) {
        this.hide(this.loadingEl);
        this.emptyLink.href = fallbackUrl;
        this.show(this.emptyEl);
      }
    }

    renderTypeOption(value) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'button button--outlined product-finder__option';
      button.setAttribute('data-product-finder-option', '');
      button.dataset.paramName = value.param_name;
      button.dataset.value = value.value;
      button.textContent = value.label;

      this.optionsContainer.append(button);
    }

    showChoices() {
      if (!this.previewVisible) return;

      this.previewVisible = false;
      this.hide(this.previewEl, () => this.show(this.choicesEl));
    }

    showPreview() {
      this.previewVisible = true;

      if (this.previewSlider.slider) {
        this.previewSlider.slider.destroy(true, true);
        this.previewSlider.slider = null;
      }

      this.previewGrid.innerHTML = '';
      this.hide(this.previewEmptyEl);
      this.hide(this.seeAllLink);

      this.hide(this.choicesEl, () => {
        this.show(this.previewEl);
        this.show(this.previewLoadingEl);
      });

      this.loadPreview();
    }

    async loadPreview() {
      const resultsUrl = this.buildResultsUrl(this.selection.finalParamName, this.selection.finalValue);

      this.seeAllLink.href = resultsUrl;
      this.seeAllLink.textContent = this.seeAllLabel;

      try {
        const previewParams = new URLSearchParams(resultsUrl.split('?')[1]);

        previewParams.set('view', 'finder-preview');

        const response = await fetch(`${this.selection.collectionUrl}?${previewParams.toString()}`);

        if (!response.ok) throw new Error('Product finder preview request failed');

        const html = await response.text();
        const fragment = document.createElement('div');

        fragment.innerHTML = html;

        const countWrapper = fragment.querySelector('[data-product-finder-preview-count]');
        const count = countWrapper ? Number(countWrapper.dataset.productFinderPreviewCount) : 0;

        this.hide(this.previewLoadingEl);
        this.show(this.seeAllLink);

        if (!countWrapper || count === 0) {
          this.show(this.previewEmptyEl);
          return;
        }

        this.previewGrid.innerHTML = countWrapper.innerHTML;
        this.previewSlider.initSlider();

        if (count > 8) {
          this.seeAllLink.textContent = `${this.seeAllLabel} (${count})`;
        }
      } catch (error) {
        this.hide(this.previewLoadingEl);
        this.show(this.previewEmptyEl);
        this.show(this.seeAllLink);
      }
    }

    buildParams() {
      const combined = [this.extraFilterParams, this.selection.audienceParams].filter(Boolean).join('&');

      return new URLSearchParams(combined);
    }

    buildResultsUrl(extraParamName, extraValue) {
      const params = this.buildParams();

      if (extraParamName) {
        params.set(extraParamName, extraValue);
      }

      return `${this.selection.collectionUrl}?${params.toString()}`;
    }

    show(el) {
      el.hidden = false;
    }

    hide(el, callback) {
      el.hidden = true;

      if (callback) callback();
    }
  }

  customElements.define('product-finder', ProductFinder);
}
